/**
 * Dashboard Page - Business Logic Layer
 *
 * Responsibilities:
 * - Data fetching (React Query hooks)
 * - State management
 * - Props preparation for template
 *
 * Does NOT contain:
 * - UI rendering (template handles that)
 * - Layout structure (PageLayout handles that)
 */

import React from 'react'
import { PageLayout } from '@/layouts/PageLayout'
import { DashboardTemplate } from '@/templates/DashboardTemplate'
import { useDashboardStats } from '@/features/dashboard/hooks/use-dashboard'
import { useAnalyticsOverview, useAnalyticsTimeseries } from '@/features/analytics/hooks/use-analytics'
import { LayoutDashboard, Activity } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

export default function DashboardPage() {
  // ========================================
  // BUSINESS LOGIC ONLY
  // ========================================

  // Date range for timeseries (last 30 days)
  const { today, thirtyDaysAgo } = React.useMemo(() => {
    const now = new Date()
    const past = new Date(now)
    past.setDate(now.getDate() - 30)
    return {
      today: now.toISOString(),
      thirtyDaysAgo: past.toISOString(),
    }
  }, [])

  // Fetch dashboard stats
  const { data: dashboardData, isLoading: statsLoading, error: statsError } = useDashboardStats()

  // Fetch analytics overview for additional metrics
  const { data: analyticsData, isLoading: analyticsLoading } = useAnalyticsOverview()

  // Fetch timeseries data for charts
  const { data: tokenTrend, isLoading: tokenTrendLoading } = useAnalyticsTimeseries('tokens', {
    start: thirtyDaysAgo,
    end: today,
  })

  const { data: signupTrend, isLoading: signupTrendLoading } = useAnalyticsTimeseries('signups', {
    start: thirtyDaysAgo,
    end: today,
  })

  const isLoading = statsLoading || analyticsLoading
  const stats = dashboardData?.data
  const analytics = analyticsData  // Backend returns data directly, not wrapped

  // ========================================
  // LAYOUT + TEMPLATE COMPOSITION
  // ========================================

  return (
    <PageLayout
      title="Dashboard"
      description="Welcome to SAIF Admin"
      icon={<LayoutDashboard className="h-6 w-6" />}
      actions={
        <Badge variant="outline" className="text-green-600 border-green-200">
          <Activity className="w-3 h-3 mr-1" />
          All systems operational
        </Badge>
      }
    >
      <DashboardTemplate
        stats={stats}
        analytics={analytics}
        tokenTrend={tokenTrend?.buckets || []}
        signupTrend={signupTrend?.buckets || []}
        isLoading={isLoading}
        tokenTrendLoading={tokenTrendLoading}
        signupTrendLoading={signupTrendLoading}
      />
    </PageLayout>
  )
}
